/**
 * Core-100 R4 wave 3 (X4) — CORE-AE F: "CORE-AE SHALL bring together the operational intelligence for one ticket —
 * … its location, its custody and its history" — and "Provenance SHALL survive composition".
 *
 * The Command Center's Location & custody section COMPOSES two authorities; it owns neither:
 *   - location truth (CORE-X / CORE-AO): `get_vehicle_location_history` — the event stream with the current row
 *     marked (`is_current`), each event carrying its source and confidence class; the plan (`ticket_location_plan`)
 *     is a separate answer and is never shown as where the vehicle is;
 *   - custody (CORE-AK): `ticket_custody` (who holds the visit now) and `ticket_custody_events` (who handed it over,
 *     when and why).
 * These pure functions turn the authorities' own vocabulary into words, and say for every fact HOW it is known —
 * a person's recorded act, a sensor's observation (with its confidence), the workflow, or an inference — so a
 * camera's guess and a person's move never look alike.
 */

export type LocationEvidence = 'person' | 'workflow' | 'sensor' | 'inferred' | 'unknown';

export interface LocationHistoryRow {
  event_id: string;
  observed_at: string;
  recorded_at?: string | null;
  location_kind: string;
  bay_id: string | null;
  bay_name: string | null;
  parking_spot_id: string | null;
  parking_spot_label: string | null;
  off_site_reason: string | null;
  source: string;
  confidence_class: string | null;
  confidence: number | null;
  actor_user_id: string | null;
  reason: string | null;
  is_current: boolean;
  // Oct 20 addendum L5 (absent on rows read before the attribution columns existed)
  moved_by_user_id?: string | null;
  mover_role?: string | null;
  recorder_role?: string | null;
  movement_attribution?: string | null;
  from_location_kind?: string | null;
  from_bay_name?: string | null;
  from_parking_spot_label?: string | null;
}

const OFF_SITE_WORDS: Record<string, string> = { TEST_DRIVE: 'on a test drive', SUBLET: 'at a sublet shop', OTHER: 'off site' };

/** Where — in words, from the location authority's own kinds. */
export function locationPlaceWords(row: Pick<LocationHistoryRow, 'location_kind' | 'bay_name' | 'parking_spot_label' | 'off_site_reason'>): string {
  switch (row.location_kind) {
    case 'BAY': return row.bay_name ? `Bay ${row.bay_name.replace(/^Bay\s*/i, '')}` : 'A bay';
    case 'PARKING_SPOT': return row.parking_spot_label ? row.parking_spot_label.replace(/^Parking\s*/i, 'Parking spot ') : 'A parking spot';
    case 'ON_SITE_GENERAL': return 'On site';
    case 'ON_SITE_UNKNOWN_EXACT': return 'On site — exact place not known';
    case 'OFF_SITE': return `Off site — ${OFF_SITE_WORDS[row.off_site_reason ?? 'OTHER'] ?? 'off site'}`;
    case 'NOT_PRESENT': return 'Not at the shop';
    default: return 'Unknown place';
  }
}

const SOURCE_WORDS: Record<string, { evidence: LocationEvidence; words: string }> = {
  HUMAN_CORRECTION: { evidence: 'person', words: 'Corrected by a person' },
  TECH_PULL_IN: { evidence: 'person', words: 'A technician pulled it in' },
  TECH_PARK: { evidence: 'person', words: 'A technician parked it' },
  FOH_MOVE: { evidence: 'person', words: 'Moved by the front office' },
  SYSTEM_WORKFLOW: { evidence: 'workflow', words: 'Recorded by the workflow' },
  CAMERA_OBSERVATION: { evidence: 'sensor', words: 'Seen by a camera' },
  LPR_OBSERVATION: { evidence: 'sensor', words: 'Plate read by a camera' },
  IMPORT: { evidence: 'workflow', words: 'Imported' },
  UNKNOWN: { evidence: 'unknown', words: 'Source not recorded' },
};

/** How this location is known — the source and the confidence class the location authority recorded. */
export function locationProvenance(row: Pick<LocationHistoryRow, 'source' | 'confidence_class' | 'confidence'>): {
  evidence: LocationEvidence;
  words: string;
} {
  const base = SOURCE_WORDS[row.source] ?? { evidence: 'unknown' as LocationEvidence, words: 'Source not recorded' };
  if (row.confidence_class === 'INFERRED') return { evidence: 'inferred', words: `${base.words} — inferred, not observed` };
  if (row.confidence_class === 'OBSERVED' || base.evidence === 'sensor') {
    const pct = typeof row.confidence === 'number' && Number.isFinite(row.confidence)
      ? ` (${Math.round(row.confidence <= 1 ? row.confidence * 100 : row.confidence)}% confidence)` : '';
    return { evidence: 'sensor', words: `${base.words} — an observation${pct}` };
  }
  return base;
}

export const CUSTODY_OWNER_WORDS: Record<string, string> = {
  BOH: 'Back of house (technician)',
  FOH: 'Front office',
  CUSTOMER: 'Customer',
};

export const CUSTODY_STATUS_WORDS: Record<string, string> = {
  with_tech: 'with the technician',
  pending_advisor_accept: 'handed to the front office — waiting for an advisor to accept',
  with_advisor: 'with an advisor',
  pending_tech_accept: 'handed back — waiting for the technician to accept',
  released_to_customer: 'picked up — the shop no longer holds the vehicle',
};

/** Who holds the visit now, in words. No custody row = no handoff recorded yet (the default position). */
export function custodyNowWords(row: { custody_owner: string; custody_status: string } | null): string {
  if (!row) return 'No handoff recorded — the visit is with the back office (default)';
  const owner = CUSTODY_OWNER_WORDS[row.custody_owner] ?? row.custody_owner;
  const status = CUSTODY_STATUS_WORDS[row.custody_status] ?? row.custody_status.replace(/_/g, ' ');
  return `${owner} — ${status}`;
}

// ── Oct 20 addendum L5 — WHO moved the vehicle, and who recorded it ─────────────────────────────────────────────
// The physical mover is shown as a fact ONLY when it was attested (by the mover themselves, or by the recorder
// naming them). A historic row (recorded before movers were recorded) names its recorder and says the mover is not
// known — an actor is never presented as the physical mover by inference.

export const MOVER_ROLE_WORDS: Record<string, string> = {
  OWNER: 'Owner',
  MANAGER: 'Manager',
  ORG_ADMIN: 'Org Admin',
  SERVICE_ADVISOR: 'Service Advisor',
  DISPATCHER: 'Dispatcher',
  TECHNICIAN: 'Technician',
  DETAIL_SPECIALIST: 'Detail Specialist',
  PARTS: 'Parts',
};

export const MOVEMENT_CLASS_WORDS: Record<string, string> = {
  TECHNICIAN_MOVEMENT: 'Technician movement',
  DETAIL_SPECIALIST_MOVEMENT: 'Detail Specialist movement',
  FOH_MOVEMENT: 'Front office movement',
  MANAGER_OWNER_MOVEMENT: 'Manager/Owner movement',
  RECORDED_ON_BEHALF: 'Recorded on behalf of another employee',
  SYSTEM_RECONCILIATION: 'System reconciliation',
  MOVER_NOT_RECORDED: 'Mover not recorded',
  RECORD_CORRECTION: 'Record correction',
};

export interface MovementAttributionView {
  /** The classification in words ("Detail Specialist movement"); historic rows: "Recorded before movers were tracked". */
  label: string;
  /** One sentence naming the mover (only when attested) and the recorder. */
  sentence: string;
  /** Whether a physical mover is on record as a fact. */
  moverConfirmed: boolean;
  /** Stable key for styling / tests: the classification, or LEGACY for historic rows. */
  key: string;
}

function personWords(id: string | null | undefined, role: string | null | undefined, names: Record<string, string>): string {
  const roleWords = role ? MOVER_ROLE_WORDS[role] ?? role.replace(/_/g, ' ').toLowerCase() : null;
  const name = id ? names[id] : undefined;
  if (name && roleWords) return `${name} (${roleWords})`;
  if (name) return name;
  if (roleWords) return `a ${roleWords}`;
  return 'an employee';
}

export function movementAttribution(
  row: Pick<LocationHistoryRow, 'actor_user_id' | 'moved_by_user_id' | 'mover_role' | 'recorder_role' | 'movement_attribution'>,
  names: Record<string, string>,
): MovementAttributionView {
  const cls = row.movement_attribution ?? null;
  const recorder = personWords(row.actor_user_id, row.recorder_role, names);
  switch (cls) {
    case 'TECHNICIAN_MOVEMENT':
    case 'DETAIL_SPECIALIST_MOVEMENT':
    case 'FOH_MOVEMENT':
    case 'MANAGER_OWNER_MOVEMENT':
      return { key: cls, label: MOVEMENT_CLASS_WORDS[cls], moverConfirmed: true,
        sentence: `Moved by ${personWords(row.moved_by_user_id, row.mover_role, names)} — recorded by them` };
    case 'RECORDED_ON_BEHALF':
      return { key: cls, label: MOVEMENT_CLASS_WORDS[cls], moverConfirmed: true,
        sentence: `Moved by ${personWords(row.moved_by_user_id, row.mover_role, names)} — recorded on their behalf by ${recorder}` };
    case 'SYSTEM_RECONCILIATION':
      return { key: cls, label: MOVEMENT_CLASS_WORDS[cls], moverConfirmed: false,
        sentence: 'Recorded by the system — no person is recorded as moving it' };
    case 'MOVER_NOT_RECORDED':
      return { key: cls, label: MOVEMENT_CLASS_WORDS[cls], moverConfirmed: false,
        sentence: `Recorded by ${recorder}; who physically moved it was not recorded` };
    case 'RECORD_CORRECTION':
      return { key: cls, label: MOVEMENT_CLASS_WORDS[cls], moverConfirmed: false,
        sentence: `Location record corrected by ${recorder} — not a recorded move` };
    default:
      return { key: 'LEGACY', label: 'Recorded before movers were tracked', moverConfirmed: false,
        sentence: row.actor_user_id
          ? `Recorded by ${recorder}; who physically moved it is not known`
          : 'Who physically moved it is not known' };
  }
}

/** "from Bay 2" — the origin the location authority recorded, or null when none was. */
export function movementOriginWords(
  row: Pick<LocationHistoryRow, 'from_location_kind' | 'from_bay_name' | 'from_parking_spot_label'>,
): string | null {
  if (!row.from_location_kind) return null;
  const where = locationPlaceWords({ location_kind: row.from_location_kind, bay_name: row.from_bay_name ?? null,
    parking_spot_label: row.from_parking_spot_label ?? null, off_site_reason: null });
  return `from ${where.charAt(0).toLowerCase()}${where.slice(1)}`;
}
